import { Button } from '#app/components/Buttons/Button.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { MailSendOutlineIcon, ReplaceIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { type FC, type ReactElement } from 'react';
import { css, styled } from 'styled-components';

const ErrorContent = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  height: 100%;

  width: 100%;
  max-width: 900px;
  margin: 0px auto;
  padding: 24px;
`;

const Section = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 420px;

    ${theme.mediaQueries.md} {
      max-width: unset;
    }
  `}
`;

const CallToActions = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 16px;

    ${theme.mediaQueries.md} {
      flex-direction: row;
    }
  `}
`;

const Headline = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    font-weight: 500;
    line-height: 120%;
    text-align: center;
    /* letter-spacing: -0.06em; */
    width: 100%;

    font-size: 19px;

    ${theme.mediaQueries.md} {
      font-size: 42px;
    }
  `}
`;

const Prompt = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};

    font-weight: 400;
    text-align: center;
    letter-spacing: -0.25px;
    font-size: 16px;

    ${theme.mediaQueries.md} {
      font-size: 23px;
      line-height: 32px;
    }

    a {
      color: ${theme.colorsV2.brandPurple};
      :hover {
        text-decoration-color: ${theme.colorsV2.brandPurple};
      }
    }
  `}
`;

type CommonErrorBoundaryProps = {
  readonly children?: ReactElement;
  readonly onReset: VoidFunction;
  readonly onSendMessage: VoidFunction;
  readonly sentryEventId?: string;
  readonly title?: string;
};

const DEFAULT_TITLE = "Having trouble? We're here to help!";

const DefaultErrorContentPrompt = () => {
  return (
    <>
      Send a message to <a href="mailto:hello@contra.com">hello@contra.com</a>{' '}
      with the problem you're facing and we will get to the bottom of it.
    </>
  );
};

export const ErrorBoundaryContent: FC<CommonErrorBoundaryProps> = ({
  children,
  sentryEventId,
  title = DEFAULT_TITLE,
}) => {
  const { track } = useAnalytics();

  const location = useLocation();

  const navigation = useNavigation();

  useMount(() => {
    track('error_boundary_viewed', {
      destination_url: location.currentURL,
      error_event_id: sentryEventId,
    });
  });

  return (
    <ErrorContent>
      <Section>
        <Headline>{title}</Headline>
        <Prompt> {children ? children : <DefaultErrorContentPrompt />}</Prompt>
      </Section>
      <Section>
        <CallToActions>
          <Button
            as="a"
            href="mailto:hello@contra.com"
            icon={MailSendOutlineIcon}
            onClick={() => {
              track('button', {
                action: 'tapped',
                button_name: 'error_boundary_send_message',
                error_event_id: sentryEventId,
              });
            }}
            variant="V2Primary"
          >
            Send us a message
          </Button>

          <Button
            icon={ReplaceIcon}
            onClick={() => {
              track('button', {
                action: 'tapped',
                button_name: 'error_boundary_reload',
                error_event_id: sentryEventId,
              });
              navigation.reload();
            }}
            variant="V2Secondary"
          >
            Reload the page
          </Button>
        </CallToActions>
      </Section>
    </ErrorContent>
  );
};
